import React, { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom'
import { CheckCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import seacureLogo from '@/assets/SEACURE(CYBER SECURITY).png'

type LocationState = { from?: { pathname?: string } } | null

export const LoginPage: React.FC = () => {
  const { user, refreshAuth } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const justReset = searchParams.get('reset') === 'success'

  const [mode, setMode] = useState<'signin' | 'signup' | 'reset'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // redirect if already authenticated
  useEffect(() => {
    if (user) {
      const state = location.state as LocationState
      const from = state?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }, [user, navigate, location])


  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)
    setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        setError(error.message)
        return
      }
      // Explicitly refresh AuthContext so user is set before we navigate
      await refreshAuth()
      const state = location.state as LocationState
      const from = state?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to sign in'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)

    if (!email || !email.trim()) {
      setError('Email address is required')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`
        }
      })

      if (signUpError) {
        setError(signUpError.message)
        return
      }

      if (signUpData.user) {
        await new Promise(resolve => setTimeout(resolve, 1000))
        window.location.reload()
        return
      }

      setMessage('Account created successfully! You can now sign in.')
      setMode('signin')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to sign up'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)
    setLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (error) {
        setError(error.message)
        return
      }
      setMessage('Password reset email sent. Please check your inbox.')
      setMode('signin')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send reset email'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  if (user) return null

  const isSignin = mode === 'signin'
  const isSignup = mode === 'signup'
  const isReset = mode === 'reset'

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-12 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img
          alt="SEACURE Cyber Security"
          src={seacureLogo}
          className="mx-auto h-40 w-auto"
        />
        <h2 className="mt-10 text-center text-2xl font-bold tracking-tight text-slate-900">
          {isSignin && 'Sign in to your account'}
          {isSignup && 'Create your account'}
          {isReset && 'Reset your password'}
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        {justReset && (
          <div className="mb-4 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800 border border-emerald-200 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            <span>Password reset. Sign in with your new password.</span>
          </div>
        )}
        {message && (
          <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-800 border border-green-200">
            {message}
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800 border border-red-200">
            {error}
          </div>
        )}

        <form className="space-y-6" onSubmit={isSignin ? handleSignIn : isSignup ? handleSignUp : handleResetPassword}>
          {/* email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email address
            </label>
            <div className="mt-2">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={isSignup ? "yourname@sea-net.co.kr" : ""}
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
              />
            </div>
          </div>

          {/* password */}
          {!isReset && (
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                {isSignin && (
                  <Link
                    to="/forgot-password"
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={isSignin ? 'current-password' : 'new-password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                />
              </div>
            </div>
          )}

          {/* confirm password for signup */}
          {isSignup && (
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
                Confirm password
              </label>
              <div className="mt-2">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                />
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
            >
              {loading ? 'Please wait…' : isSignin ? 'Sign in' : isSignup ? 'Create account' : 'Send reset link'}
            </button>
          </div>
        </form>

        {/* links */}
        <p className="mt-10 text-center text-sm text-slate-600">
          {isSignin && (
            <span className="text-slate-500">
              SEACURE DB is invite-only. Contact your administrator for access.
            </span>
          )}

          {isReset && (
            <>
              Remembered your password?
              <button
                type="button"
                onClick={() => {
                  setMode('signin')
                  setMessage(null)
                  setError(null)
                }}
                className="ml-1 font-semibold text-indigo-600 hover:text-indigo-700"
              >
                Back to sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  )
}